import { __rest, __assign } from 'tslib';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CloseIcon } from '@panwds/icons';
import { config, useTransition, animated } from '@react-spring/web';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { getActualWidth } from '../../utils/getActualWidth.esm.js';
import { getTransitionConfig } from '../../utils/getTransitionConfig/getTransitionConfig.esm.js';
import '@popperjs/core';
import { testIdGen } from '../../utils/testIdGen.esm.js';
import { useDimensions } from '../../utils/useDimensions/useDimensions.esm.js';
import { Button } from '../Button/Button.esm.js';
import '../Icon/ImgIcon.esm.js';
import '../Icon/FaIcon.esm.js';
import '../Icon/SemanticIcons.esm.js';
import '../Button/ProgressButton.esm.js';
import '../Button/CopyButton.esm.js';
import { Body } from '../Typography/Body.esm.js';
import '../Tooltip/Tooltip.esm.js';
import { Title } from '../Typography/Title.esm.js';
import { useSheetId } from './useSheetId/useSheetId.esm.js';
import { useUniqueId } from './useUniqueId/useUniqueId.esm.js';

var sheetPositionSizes = {
    sm: { minWidth: 360, maxWidth: 720, divide: 4, dir: "right" },
    md: { minWidth: 480, maxWidth: 960, divide: 3, dir: "right" },
    lg: { minWidth: 720, maxWidth: 1440, divide: 2, dir: "right" },
    xl: { minWidth: 960, maxWidth: 1920, divide: 1.5, dir: "right" },
    permanent: { minWidth: 360, maxWidth: 720, divide: 4, dir: "left" },
    bottom: { height: 256, dir: "bottom" }
};
/** This component renders a Sheet to right/bottom/left.
 *
 */
function Sheet(_a) {
    var children = _a.children, dataMetrics = _a.dataMetrics, dataTestId = _a.dataTestId, title = _a.title, subtitle = _a.subtitle, actions = _a.actions, _b = _a.isOpen, isOpen = _b === void 0 ? false : _b, onClose = _a.onClose, _c = _a.enableClose, enableClose = _c === void 0 ? true : _c, _d = _a.position, position = _d === void 0 ? "sm" : _d, _e = _a.leftRef, leftRef = _e === void 0 ? null : _e, _f = _a.showMask, showMask = _f === void 0 ? false : _f, passThrough = __rest(_a, ["children", "dataMetrics", "dataTestId", "title", "subtitle", "actions", "isOpen", "onClose", "enableClose", "position", "leftRef", "showMask"]);
    var id = useUniqueId();
    var _g = useSheetId(), sheetId = _g[0], setSheetId = _g[1];
    useEffect(function () {
        return function () {
            if (sheetId === id)
                setSheetId("");
        };
    }, [sheetId, id, setSheetId]);
    useEffect(function () {
        if (sheetId !== "" && sheetId !== id) {
            onClose();
        }
    }, [sheetId, id, onClose]);
    useEffect(function () {
        if (isOpen && sheetId === "") {
            setSheetId(id);
        }
        if (!isOpen && sheetId === id) {
            setSheetId("");
        }
    }, [isOpen, id, setSheetId, sheetId]);
    var opened = useMemo(function () {
        return sheetId === id;
    }, [sheetId, id]);
    var intl = useIntl();
    var sizeObj = sheetPositionSizes[position];
    var _h = useDimensions({ ref: leftRef }), navWidth = _h.width, navHeight = _h.height, navTop = _h.top, navLeft = _h.left;
    var sheetPosition = {
        left: "".concat((navWidth !== null && navWidth !== void 0 ? navWidth : 0) + (navLeft !== null && navLeft !== void 0 ? navLeft : 0), "px"),
        top: "".concat(navTop !== null && navTop !== void 0 ? navTop : 0, "px"),
        height: "".concat(navHeight !== null && navHeight !== void 0 ? navHeight : 0, "px")
    };
    var screenWidth = useDimensions().width;
    var actualWidth = useMemo(function () { return getActualWidth(screenWidth !== null && screenWidth !== void 0 ? screenWidth : 0, sizeObj); }, [sizeObj, screenWidth]);
    var transitionConfigs = useMemo(function () {
        var size = {};
        if (position === "bottom") {
            size.height = sheetPositionSizes[position].height;
        }
        else {
            size.width = actualWidth;
        }
        return getTransitionConfig(size, config.stiff);
    }, [actualWidth, config, position]);
    var SheetClasses = classNames("tw-box-border tw-font-lato tw-bg-white tw-shadow tw-border-l tw-z-10 dark:tw-bg-gray-1000 dark:tw-border-gray-700", "tw-flex tw-flex-col tw-overflow-hidden", "tw-h-full tw-fixed", "tw-border-t", position === "bottom" ? "tw-bottom-0 tw-right-0" : "tw-right-0 tw-top-0");
    var transitions = useTransition(opened, transitionConfigs);
    var datatestid = dataTestId || (title && "sheet-".concat(testIdGen(title)));
    return (React.createElement(React.Fragment, null,
        showMask && (React.createElement(OverlayMask, { isOpen: isOpen, onClick: onClose, label: intl.formatMessage({ id: "tRQMci", defaultMessage: [{ type: 0, value: "Close" }] }) })),
        transitions(function (styles, item) {
            return item && (React.createElement(animated.div, __assign({ "data-metrics": dataMetrics, "data-testid": datatestid, style: __assign(__assign(__assign({}, styles), (position === "permanent" && sheetPosition)), (position === "bottom" && {
                    left: sheetPosition.left
                })), className: SheetClasses }, passThrough),
                React.createElement("div", { className: "tw-flex tw-flex-col tw-h-full" },
                    React.createElement("div", { className: classNames("tw-border-0 tw-border-solid tw-border-gray-200 tw-flex tw-justify-between tw-px-4 tw-py-2 tw-space-x-2 tw-border-b dark:tw-border-gray-700", "tw-border-t") },
                        React.createElement("div", { 
                            // Both tw-min-w-0 here and below are required for text truncating to work
                            className: "tw-flex tw-space-x-1 tw-items-center tw-min-w-0" },
                            React.createElement("div", { className: "tw-min-w-0 tw-flex tw-flex-col" },
                                React.createElement(Title, { level: 2, size: "xxs", addClassName: "tw-flex" }, title),
                                subtitle && (React.createElement(Body, { size: "sm", addClassName: "tw-flex tw-text-secondary" }, subtitle)))),
                        React.createElement("div", { className: "tw-flex tw-space-x-2" },
                            actions,
                            enableClose && (React.createElement(Button, { onClick: onClose, appearance: "tertiary-clear", "aria-label": intl.formatMessage({ id: "tRQMci", defaultMessage: [{ type: 0, value: "Close" }] }), size: "sm", icon: React.createElement(CloseIcon, { size: "xs" }) })))),
                    React.createElement("div", { className: "tw-flex tw-flex-col tw-overflow-auto tw-flex-grow" }, children))));
        })));
}
/**
 * The OverlayMask component is used to create a mask, often behind a Modal or some other popup.
 */
function OverlayMask(_a) {
    var isOpen = _a.isOpen, label = _a.label, onClick = _a.onClick;
    var transitions = useTransition(isOpen, {
        from: { opacity: 0, zIndex: 10 },
        enter: { opacity: 0.5 },
        leave: { opacity: 0 },
        config: { clamp: true }
    });
    var classes = classNames("tw-inset-0 tw-bg-black tw-fixed", onClick && "tw-cursor-default");
    return transitions(function (styles, item) {
        return item && (React.createElement(animated.div, { style: styles, className: classes, "aria-label": label, onClick: onClick }));
    });
}

export { OverlayMask, Sheet, sheetPositionSizes };
//# sourceMappingURL=Sheet.esm.js.map
